"use client"

import { useEffect, useRef } from "react"
import type { PersistedRsvpStatus } from "@/lib/events/types"
import {
  clearPendingEventAction,
  consumePendingActionAfterSuccess,
  getPendingActionForCurrentPage,
  readPendingEventAction,
  type PendingEventActionV2,
} from "@/lib/auth/pending-event-action"

type ReplayRouter = {
  replace: (href: string, opts?: { scroll?: boolean }) => void
}

/** Dedupe replay across Strict Mode and concurrent effects (per event + nonce / legacy key). */
const replayInflight = new Map<string, Promise<void>>()

type PendingEventReplayDeps = {
  router: ReplayRouter
  eventId: string
  /** When still loading or missing, host cleanup must not wipe a valid pending intent */
  event: { id: string } | null
  isLoading: boolean
  authLoaded: boolean
  userId: string | null | undefined
  /** Guest viewer context — skip for hosts / demo */
  canReplayGuestActions: boolean
  /** Used only to drop stale guest intents when the viewer is the host */
  isUserHosting: boolean
  timelineLoaded: boolean
  /** Legacy `save_timeline=1` query (still honored when no session pending). */
  saveTimelineQueryIntent: string
  /** Execute timeline save; return true on success */
  replayTimelineSave: () => Promise<boolean>
  /** Returns true if already saved (idempotent) */
  isTimelineSaved: (eventId: string) => boolean
  /** RSVP: set status; return ok / already / fail */
  replayRsvp: (
    status: PersistedRsvpStatus,
  ) => Promise<"ok" | "already" | "fail">
  /** Optional: attend API for future CTAs */
  replayAttend?: () => Promise<boolean>
  onTimelineSuccess: (message: string) => void
  onRsvpSuccess: (message: string) => void
  onTimelineError: (message: string) => void
  onRsvpError: (message: string) => void
  stripSaveTimelineQueryFromUrl: () => void
}

function stripAuthIntentParamsFromUrl(router: ReplayRouter) {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  let changed = false
  if (url.searchParams.has("save_timeline")) {
    url.searchParams.delete("save_timeline")
    changed = true
  }
  if (url.searchParams.has("auth_intent_ts")) {
    url.searchParams.delete("auth_intent_ts")
    changed = true
  }
  if (!changed) return
  const qs = url.searchParams.toString()
  const next = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`
  router.replace(next, { scroll: false })
}

export function usePendingEventReplay(deps: PendingEventReplayDeps): void {
  const depsRef = useRef(deps)
  depsRef.current = deps

  useEffect(() => {
    const d = depsRef.current
    if (d.isLoading || !d.authLoaded || !d.userId?.trim()) return

    if (!d.canReplayGuestActions) {
      if (d.isLoading || !d.event) return
      if (!d.isUserHosting) return
      const stale = readPendingEventAction()
      if (stale?.eventId === d.eventId) {
        clearPendingEventAction()
        stripAuthIntentParamsFromUrl(d.router)
      }
      return
    }

    const pending = getPendingActionForCurrentPage(d.eventId)
    const legacyTimeline =
      !pending &&
      (d.saveTimelineQueryIntent.toLowerCase() === "1" ||
        d.saveTimelineQueryIntent.toLowerCase() === "true")

    if (!pending && !legacyTimeline) return
    if (!d.timelineLoaded && (legacyTimeline || pending?.kind === "timeline_save")) {
      return
    }

    const key = pending
      ? `${d.eventId}:${pending.kind}:${pending.nonce}`
      : `${d.eventId}:legacy:save_timeline`

    let existing = replayInflight.get(key)
    if (!existing) {
      existing = (async () => {
        const run = depsRef.current
        try {
          if (pending) {
            await runSessionPending(pending, run)
          } else if (legacyTimeline) {
            await runLegacyTimeline(run)
          }
        } finally {
          replayInflight.delete(key)
        }
      })()
      replayInflight.set(key, existing)
    }
    void existing
  }, [
    deps.isLoading,
    deps.authLoaded,
    deps.userId,
    deps.eventId,
    deps.event,
    deps.canReplayGuestActions,
    deps.isUserHosting,
    deps.timelineLoaded,
    deps.saveTimelineQueryIntent,
  ])
}

async function runLegacyTimeline(deps: PendingEventReplayDeps): Promise<void> {
  if (deps.isTimelineSaved(deps.eventId)) {
    deps.onTimelineSuccess("Already in your timeline.")
    stripAuthIntentParamsFromUrl(deps.router)
    return
  }
  const ok = await deps.replayTimelineSave()
  if (ok) {
    deps.onTimelineSuccess("Added to your timeline.")
    stripAuthIntentParamsFromUrl(deps.router)
  } else {
    deps.onTimelineError("Can't add this event to your timeline right now.")
    stripAuthIntentParamsFromUrl(deps.router)
  }
}

async function runSessionPending(
  pending: PendingEventActionV2,
  deps: PendingEventReplayDeps,
): Promise<void> {
  switch (pending.kind) {
    case "timeline_save": {
      if (!deps.timelineLoaded) return
      if (deps.isTimelineSaved(deps.eventId)) {
        deps.onTimelineSuccess("Already in your timeline.")
        consumePendingActionAfterSuccess(pending.nonce)
        stripAuthIntentParamsFromUrl(deps.router)
        return
      }
      const ok = await deps.replayTimelineSave()
      if (ok) {
        deps.onTimelineSuccess("Added to your timeline.")
        consumePendingActionAfterSuccess(pending.nonce)
        stripAuthIntentParamsFromUrl(deps.router)
      } else {
        deps.onTimelineError("Can't add this event to your timeline right now. Try again.")
        clearPendingEventAction()
        stripAuthIntentParamsFromUrl(deps.router)
      }
      break
    }
    case "event_rsvp": {
      const status = pending.rsvpStatus
      if (!status) {
        consumePendingActionAfterSuccess(pending.nonce)
        return
      }
      const result = await deps.replayRsvp(status)
      if (result === "ok") {
        const labels: Record<PersistedRsvpStatus, string> = {
          going: "You're marked as going.",
          maybe: "Marked as maybe.",
          declined: "You're not going.",
        }
        deps.onRsvpSuccess(labels[status])
        consumePendingActionAfterSuccess(pending.nonce)
        stripAuthIntentParamsFromUrl(deps.router)
      } else if (result === "already") {
        deps.onRsvpSuccess(
          status === "going"
            ? "You're marked as going."
            : status === "maybe"
              ? "Marked as maybe."
              : "You're not going.",
        )
        consumePendingActionAfterSuccess(pending.nonce)
        stripAuthIntentParamsFromUrl(deps.router)
      } else {
        deps.onRsvpError("Couldn't save RSVP. Try again.")
        clearPendingEventAction()
        stripAuthIntentParamsFromUrl(deps.router)
      }
      break
    }
    case "event_attend": {
      if (!deps.replayAttend) {
        consumePendingActionAfterSuccess(pending.nonce)
        return
      }
      const ok = await deps.replayAttend()
      if (ok) {
        deps.onTimelineSuccess("You're attending.")
        consumePendingActionAfterSuccess(pending.nonce)
        stripAuthIntentParamsFromUrl(deps.router)
      } else {
        deps.onTimelineError("Couldn't update attendance. Try again.")
        clearPendingEventAction()
        stripAuthIntentParamsFromUrl(deps.router)
      }
      break
    }
    default:
      consumePendingActionAfterSuccess(pending.nonce)
  }
}
